import { LiveAnnouncer } from '@angular/cdk/a11y';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  input,
  Input,
  OnInit,
  output,
  Output,
} from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  AutocompleteComponent,
  InputDirective,
  LabelDirective,
  OptionDirective,
  SelectComponent,
  ValidationMessageComponent,
  ValidationMessagesComponent,
} from '@shared/ui-forms';
import { BoldPipe } from '@shared/ui-shared';
import {
  debounceTime,
  distinctUntilChanged,
  iif,
  map,
  Observable,
  of,
  Subject,
  Subscription,
  switchMap,
  tap,
} from 'rxjs';

import { USA_STATES } from '../../constants/usa-states.constant';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PlacePrediction } from '../../models/place-prediction.model';
import { ResidentialAddress } from '../../models/residential-address.model';
import { GoogleMapService } from '../../services/google-map.service';
import { emptyStringValidator } from '../../validators/empty-string.validator';
import { requiredAddressValidator } from '../../validators/required-address.validator';

const CSL_IMPORTS = [
  InputDirective,
  SelectComponent,
  AutocompleteComponent,
  OptionDirective,
  ValidationMessagesComponent,
  ValidationMessageComponent,
  LabelDirective,
  BoldPipe,
];

@Component({
  selector: 'csl-address-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ...CSL_IMPORTS],
  templateUrl: './address-form.component.html',
  styleUrl: './address-form.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [GoogleMapService],
})
export class AddressFormComponent implements OnInit {
  private readonly geoService = inject(GoogleMapService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly liveAnnouncer = inject(LiveAnnouncer);

  private streetRequiredValidator = requiredAddressValidator('street');
  private detailsSubscription?: Subscription;

  readonly errorMessages = {
    street: 'Street address is required. Start typing to find and select a matching address.',
    street2: '',
    poBox: 'PO Box address is required.',
    city: 'City is required. Please select from the suggested options.',
    zip: 'Zip code is required. Please select from the suggested options.',
    state: 'State is required.',
  };

  readonly errorIds: Record<AddressFormControlName, string> = {
    street: 'street-error',
    street2: 'street2-error',
    poBox: 'poBox-error',
    city: 'city-error',
    zip: 'zip-error',
    state: 'state-error',
  };

  states = USA_STATES;
  form: AddressForm = this.initForm();
  zipCodePattern = /^[0-9\\-]+$/;

  emitInitialValue = input<boolean>(false);

  @Input()
  public set address(value: ResidentialAddress | null | undefined) {
    if (value) {
      this.patchAddress(value);
    } else {
      this.form.patchValue(
        { street: null, street2: null, poBox: null, city: null, zip: null, state: null },
        { emitEvent: false },
      );
    }
  }

  private _withPoBox = false;
  @Input()
  public get withPoBox() {
    return this._withPoBox;
  }
  public set withPoBox(value) {
    this._withPoBox = value;
    this.handlePoBoxSetting(value);
  }

  @Input() set isDisabled(v: boolean) {
    if (v) {
      this.form.disable({ emitEvent: false });
    } else {
      this.form.enable();
    }
  }

  @Output() addressChange = this.form.valueChanges.pipe(
    map((v) =>
      this.form.valid
        ? {
            street: v.street?.street,
            street2: v.street2,
            poBoxNumber: v.poBox,
            city: v.city?.city,
            state: v.state,
            zip: v.zip?.zip,
            geoLocation: v.street?.geoLocation,
          }
        : null,
    ),
  ) as Observable<ResidentialAddress | null>;

  cityChanged = output<Partial<ResidentialAddress> | string | null>();
  stateChanged = output<Partial<ResidentialAddress> | string | null>();
  zipChanged = output<Partial<ResidentialAddress> | string | null>();

  @Output() streetError = this.emitControlError('street');
  @Output() cityError = this.emitControlError('city');
  @Output() zipError = this.emitControlError('zip');
  @Output() stateError = this.emitControlError('state');

  constructor() {
    this.form.controls.street.valueChanges.subscribe((value) =>
      this.updateAddressFields(value, 'Address selected. City, state, and zip code updated.'),
    );
    this.form.controls.city.valueChanges.subscribe((value) =>
      this.updateAddressFields(value, 'City selected. Street address, state, and zip code updated.'),
    );
    this.form.controls.zip.valueChanges.subscribe((value) =>
      this.updateAddressFields(value, 'Zip code selected. Street address, city, and state updated.'),
    );

    this.form.controls.state.valueChanges.subscribe(() => {
      this.form.patchValue(
        {
          street: null,
          city: null,
          zip: null,
        },
        { emitEvent: false },
      );
      this.liveAnnouncer.announce('State updated. Street address, city, and zip code cleared.', 'polite');
    });
  }

  ngOnInit(): void {
    this.emitControlValue('city').subscribe((city) => this.cityChanged.emit(city));
    this.emitControlValue('state').subscribe((state) => this.stateChanged.emit(state));
    this.emitControlValue('zip').subscribe((zip) => this.zipChanged.emit(zip));
  }

  private handlePoBoxSetting(withPoBox: boolean) {
    if (this.form.disabled) return;

    const streetControl = this.form.controls.street;
    const poBoxControl = this.form.controls.poBox;
    if (withPoBox) {
      streetControl.removeValidators(this.streetRequiredValidator);
      streetControl.reset(null, { emitEvent: false });
      poBoxControl.addValidators(emptyStringValidator);
      poBoxControl.updateValueAndValidity();
    } else {
      poBoxControl.removeValidators(emptyStringValidator);
      poBoxControl.reset(null, { emitEvent: false });
      streetControl.addValidators(this.streetRequiredValidator);
      streetControl.updateValueAndValidity();
    }
  }

  private patchAddress(address: Partial<ResidentialAddress>): void {
    const emitEvent = this.emitInitialValue() && this.form.enabled;
    this.form.patchValue(
      {
        street: address,
        street2: address.street2,
        poBox: address.poBoxNumber,
        city: address,
        state: address.state,
        zip: address,
      },
      { emitEvent },
    );

    if (this.form.controls.street.invalid) {
      this.form.controls.street.markAsTouched();
    }
  }

  private emitControlError(controlName: AddressFormControlName): Observable<string> {
    return this.form.controls[controlName].events.pipe(
      map(({ source }) => source),
      map((ctrl) => (this.isValidationShouldBeShown(ctrl) ? this.errorMessages[controlName] : '')),
      distinctUntilChanged(),
    );
  }

  private emitControlValue(
    controlName: AddressFormControlName,
  ): Observable<Partial<ResidentialAddress> | string | null> {
    return this.form.get(controlName)!.valueChanges.pipe(takeUntilDestroyed(this.destroyRef));
  }

  private updateAddressFields(value: AddressControlValue | null, announcement: string): void {
    if (!value) {
      return;
    }

    if (value.prediction) {
      this.loadPlaceDetails(value.prediction, announcement);
      return;
    }

    this.applyAddress(value, announcement);
  }

  private loadPlaceDetails(prediction: PlacePrediction, announcement: string): void {
    this.detailsSubscription?.unsubscribe();
    this.detailsSubscription = this.geoService
      .getPlaceDetails(prediction.place)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((place) => {
        if (!place) {
          return;
        }

        this.applyAddress(this.geoService.getAddressFields(place), announcement);
        this.form.updateValueAndValidity();
        this.cdr.markForCheck();
      });
  }

  private applyAddress(value: Partial<ResidentialAddress>, announcement: string): void {
    this.form.patchValue(
      {
        street: value,
        city: value,
        zip: value,
        state: value.state,
      },
      { emitEvent: false },
    );
    this.liveAnnouncer.announce(announcement, 'polite');
  }

  initForm(): AddressForm {
    return new FormGroup({
      street: new FormControl<AddressControlValue | null>(null, this.streetRequiredValidator),
      street2: new FormControl<string | null>(null),
      poBox: new FormControl<string | null>(null),
      city: new FormControl<AddressControlValue | null>(null, requiredAddressValidator('city')),
      zip: new FormControl<AddressControlValue | null>(null, requiredAddressValidator('zip')),
      state: new FormControl<string | null>(null, Validators.required),
    }) as AddressForm;
  }

  isValidationShouldBeShown(control: AbstractControl): boolean {
    return control.invalid && control.touched;
  }

  streetInput$ = new Subject<string>();
  streetMinInput = 3;
  isStreetLoading = false;
  googleStreets$ = (v: string) =>
    of(v).pipe(
      tap(() => {
        this.isStreetLoading = true;
      }),
      map((streetInput) => {
        const { city, state } = this.form.value;
        return [city?.city, state, streetInput].filter(Boolean).join(',');
      }),
      switchMap((v) => this.geoService.placesAutocomplete(v)),
      map((predictions) => predictions.map(toOption)),
      tap(() => {
        this.isStreetLoading = false;
      }),
    );
  streets$ = this.streetInput$.pipe(
    debounceTime(300),
    tap(() => this.cdr.markForCheck()), // show loading after debounceTime
    switchMap((v) => iif(() => v?.length >= this.streetMinInput, this.googleStreets$(v), of([]))),
  );
  streetDisplayFn = (addr: AddressControlValue | null) => addr?.street ?? addr?.prediction?.description ?? '';
  suggestionDisplayFn = (addr: AddressControlValue) => addr?.prediction?.description ?? '';

  cityInput$ = new Subject<string>();
  cityMinInput = 2;
  isCityLoading = false;
  googleCities$ = (v: string) =>
    of(v).pipe(
      tap(() => {
        this.isCityLoading = true;
      }),
      switchMap((v) => this.geoService.placesAutocomplete(v, 'csl-cities')),
      map((predictions) => predictions.map(toOption)),
      tap(() => {
        this.isCityLoading = false;
      }),
    );
  cities$ = this.cityInput$.pipe(
    debounceTime(300),
    tap(() => this.cdr.markForCheck()), // show loading after debounceTime
    switchMap((v) => iif(() => v?.length >= this.cityMinInput, this.googleCities$(v), of([]))),
  );
  cityDisplayFn = (addr: AddressControlValue | null) => addr?.city ?? addr?.prediction?.description ?? '';

  zipInput$ = new Subject<string>();
  isZipLoading = false;
  zipMinInput = 3;
  googleZipCodes$ = (v: string) =>
    of(v).pipe(
      tap(() => {
        this.isZipLoading = true;
      }),
      switchMap((v) => this.geoService.placesAutocomplete(v, 'postal_code')),
      map((predictions) => predictions.map(toOption)),
      tap(() => {
        this.isZipLoading = false;
      }),
    );
  zipCodes$ = this.zipInput$.pipe(
    debounceTime(300),
    tap(() => this.cdr.markForCheck()), // show loading after debounceTime
    switchMap((v) => iif(() => v?.length >= this.zipMinInput, this.googleZipCodes$(v), of([]))),
  );
  zipDisplayFn = (addr: AddressControlValue | null) => addr?.zip ?? addr?.prediction?.description ?? '';

  stateDisplayFn = (state: State | null) => {
    if (!state) return '';
    return [state.name, state.abbreviation].join(', ');
  };
}

type State = { name: string; abbreviation: string };

// A selected suggestion carries only `prediction` until its place details are loaded.
type AddressControlValue = Partial<ResidentialAddress> & { prediction?: PlacePrediction };

const toOption = (prediction: PlacePrediction): AddressControlValue => ({ prediction });

type AddressFormControls = {
  street: FormControl<AddressControlValue | null>;
  street2: FormControl<string | null>;
  poBox: FormControl<string | null>;
  city: FormControl<AddressControlValue | null>;
  zip: FormControl<AddressControlValue | null>;
  state: FormControl<string | null>;
};

export type AddressForm = FormGroup<AddressFormControls>;
type AddressFormControlName = keyof AddressFormControls;
